import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";

/** 44px on touch; fine pointers keep the compact default heights. */
const TOUCH_TARGET_TRIGGER_CLASS = "min-h-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-9";
const TOUCH_TARGET_ITEM_CLASS =
  "cursor-pointer min-h-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-8";

export interface PersonaOpportunitySelectionActionsMenuProps {
  selectedCount: number;
  isGenerating: boolean;
  canGenerateScripts: boolean;
  onClearSelection: () => void;
  onGenerateScripts: () => void;
}

/**
 * Selection-gated bulk actions for the Opportunities toolbar — Generate Scripts + Clear only.
 */
export function PersonaOpportunitySelectionActionsMenu({
  selectedCount,
  isGenerating,
  canGenerateScripts,
  onClearSelection,
  onGenerateScripts,
}: PersonaOpportunitySelectionActionsMenuProps) {
  const { t } = useTranslation();

  if (selectedCount <= 0) return null;

  const selectedLabel =
    selectedCount === 1
      ? t("personaOpportunities.bulk.selected", "1 selected")
      : t("personaOpportunities.bulk.selectedPlural", {
          defaultValue: "{{count}} selected",
          count: selectedCount,
        });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          iconStart="sparkles"
          className={TOUCH_TARGET_TRIGGER_CLASS}
          aria-label={t("personaOpportunities.bulk.barAria", {
            defaultValue: "Bulk actions for {{count}} selected opportunities",
            count: selectedCount,
          })}
          data-testid="persona-opportunity-selection-actions-menu"
        >
          {selectedLabel}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        {canGenerateScripts ? (
          <DropdownMenuItem
            className={TOUCH_TARGET_ITEM_CLASS}
            disabled={isGenerating}
            onSelect={() => onGenerateScripts()}
            data-testid="persona-bulk-generate-scripts"
          >
            {isGenerating ? (
              <span className="inline-flex items-center gap-2">
                <Icon name="loader" size="xs" className="animate-spin shrink-0" aria-hidden />
                {t("personaOpportunities.bulk.generatingScripts", "Generating scripts…")}
              </span>
            ) : (
              t("personaOpportunities.bulk.generateScripts", "Generate Scripts")
            )}
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem
          className={TOUCH_TARGET_ITEM_CLASS}
          onSelect={() => onClearSelection()}
          data-testid="persona-bulk-clear-selection"
        >
          {t("personaOpportunities.bulk.clearSelection", "Clear selection")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
